import { createContext, useContext } from "react";
import { isNil, omitBy } from "lodash";

export const CHIP_KINDS = {
  STEP: "step",
  STATE: "state",
  BLOCK: "block",
  FIELD: "field",
};

export const LEAVE_REASONS = {
  GENERATING: "GENERATING",
  PROPOSALS: "PROPOSALS",
  CODE: "CODE",
};

export const MAX_CHIPS = 4;

const chipTarget = ({ kind, label, slotId }) => {
  if (!isNil(slotId)) return slotId;
  return kind === CHIP_KINDS.FIELD ? label || "" : "";
};

export const chipKey = (chip) =>
  [chip.kind, chip.stepId, chip.stateId || "", chipTarget(chip)].join(":");

export const chipOf = ({ kind, label, stepId, stateId, slotId }) =>
  omitBy({ kind, label, stepId, stateId, slotId }, isNil);

export const withChip = (chips = [], chip) => {
  const key = chipKey(chip);
  return [...chips.filter((item) => chipKey(item) !== key), chipOf(chip)].slice(
    -MAX_CHIPS,
  );
};

const PREVIEW_KINDS = [CHIP_KINDS.BLOCK, CHIP_KINDS.FIELD];

export const withOnlyBlock = (chips = [], chip) =>
  withChip(
    chips.filter((item) => !PREVIEW_KINDS.includes(item.kind)),
    chip,
  );

export const withoutChip = (chips = [], chip) => {
  const key = chipKey(chip);
  return chips.filter((item) => chipKey(item) !== key);
};

export const withMovedStates = (chips = [], { stepId, moves = {} }) =>
  chips.reduce(
    (list, chip) =>
      withChip(
        list,
        chip.stepId === stepId && !isNil(moves[chip.stateId])
          ? { ...chip, stateId: moves[chip.stateId] }
          : chip,
      ),
    [],
  );

export const citedSlotIds = (chips = [], { stepId, stateId = null }) =>
  chips
    .filter(
      (chip) =>
        [CHIP_KINDS.BLOCK, CHIP_KINDS.FIELD].includes(chip.kind) &&
        chip.stepId === stepId &&
        (chip.stateId || null) === stateId &&
        !isNil(chip.slotId),
    )
    .map((chip) => chip.slotId);

const noop = () => undefined;

export const CanvasSelectionContext = createContext({
  stepId: null,
  stateId: null,
  chips: [],
  selectStep: noop,
  selectState: noop,
  moveStates: noop,
  guardLeave: () => noop,
  cite: noop,
  citeOnly: noop,
  uncite: noop,
});

export const useCanvasSelection = () => useContext(CanvasSelectionContext);
